import { useCallback, useEffect, useState } from 'react';
import monitoring from '../common/libs/monitoring';
import { CommentDiscloseScope } from '../common/libs/commentDiscloseScope';
import useApiClient from './useApiClient';

export type CommentUser = {
  userId: number;
  discloseId: string | null;
  userName: string | null;
  lastName: string | null;
  firstName: string | null;
  hasProfileImage: boolean;
  idBaseProfileUrl: string | null;
  updateDateEpochMilliseconds: number;
};

export type ArticleCommentReply = {
  id: number;
  user: CommentUser;
  message: string;
  isDeleted: boolean;
  createDate: string; // ISO8601
  updateDate: string; // ISO8601
  createDateFormatted: string; // YYYY/MM/DD
  updateDateFormatted: string; // YYYY/MM/DD
  updateDateEpochMilliseconds: number;
};

export type ArticleComment = {
  id: number;
  user: CommentUser;
  message: string;
  page: number;
  discloseScope: CommentDiscloseScope;
  isDeleted: boolean;
  createDate: string; // ISO8601
  updateDate: string; // ISO8601
  createDateFormatted: string; // YYYY/MM/DD
  updateDateFormatted: string; // YYYY/MM/DD
  updateDateEpochMilliseconds: number;
  replies: ArticleCommentReply[];
};

export default function useArticleComment(articleId: string) {
  const { apiClient } = useApiClient();
  const [articleCommentCount, setArticleCommentCount] = useState<number>(0);
  const [articleComments, setArticleComments] = useState<ArticleComment[]>([]);
  useEffect(() => {
    setArticleComments([]);
  }, [articleId]);

  const getArticleCommentCount = useCallback(async () => {
    try {
      const res = await apiClient(
        `/api/article/${articleId}/comments?countOnly=true`,
        {
          method: 'GET',
        },
      );
      if (res.ok) {
        const data = await res.json();
        setArticleCommentCount(data.count);
      } else {
        throw new Error();
      }
    } catch (e) {
      monitoring.notify({
        name: `API ERROR DETECTED ${e}`,
        message: 'スライドコメント件数の取得に失敗しました。',
      });
    }
  }, [articleId, apiClient]);

  const fetchArticleComments = useCallback(async () => {
    try {
      const res = await apiClient(`/api/article/${articleId}/comments`, {
        method: 'GET',
      });
      if (res.ok) {
        const data = await res.json();
        setArticleComments(data);
      } else {
        throw new Error();
      }
    } catch (e) {
      monitoring.notify({
        name: `API ERROR DETECTED ${e}`,
        message: 'スライドコメントリストの取得に失敗しました。',
      });
    }
  }, [articleId, apiClient]);

  const postArticleComment = useCallback(
    async (
      message: string,
      discloseScope?: CommentDiscloseScope,
      pageNumber?: number,
    ) => {
      try {
        const res = await apiClient(`/api/article/${articleId}/comments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message,
            discloseScope,
            pageNumber,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          return data.grantedBadges;
        } else {
          throw new Error();
        }
      } catch {
        throw new Error();
      }
    },
    [articleId, apiClient],
  );

  const deleteArticleComment = useCallback(
    async (commentId: number) => {
      try {
        const res = await apiClient(
          `/api/article/${articleId}/comments/${commentId}`,
          {
            method: 'DELETE',
          },
        );
        if (!res.ok) {
          throw new Error();
        }
      } catch {
        throw new Error();
      }
    },
    [articleId, apiClient],
  );

  const postArticleCommentReply = useCallback(
    async (commentId: number, message: string) => {
      try {
        const res = await apiClient(
          `/api/article/${articleId}/comments/${commentId}/replies`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message,
            }),
          },
        );
        if (res.ok) {
          const data = await res.json();
          return data.grantedBadges;
        } else {
          throw new Error();
        }
      } catch {
        throw new Error();
      }
    },
    [apiClient, articleId],
  );

  const deleteArticleCommentReply = useCallback(
    async (commentId: number, replyId: number) => {
      try {
        const res = await apiClient(
          `/api/article/${articleId}/comments/${commentId}/replies/${replyId}`,
          {
            method: 'DELETE',
          },
        );
        if (!res.ok) {
          throw new Error();
        }
      } catch {
        throw new Error();
      }
    },
    [apiClient, articleId],
  );

  return {
    articleCommentCount,
    articleComments,
    getArticleCommentCount,
    fetchArticleComments,
    postArticleComment,
    deleteArticleComment,
    postArticleCommentReply,
    deleteArticleCommentReply,
  };
}
